import { AudienceDefinitions } from "../../../../dtos/AudienceDefinitions";
import { RoleOptionDto } from "../../../../dtos/RoleOptionDto";
import { UserRole } from "../../../../dtos/UserRole";

// Roles that can be selected for the NIH audience in template configuration
export const allowedNihRoles: UserRole[] = [
    UserRole.ORF_DTR,
    UserRole.ORF_DEP,
    UserRole.PO,
    UserRole.GMS,
    UserRole.PO_SUPERVISOR,
    UserRole.GMS_SUPERVISOR,
    UserRole.ORF_DTR_SUPERVISOR,
    UserRole.SU,
];

// Roles that can be selected for the Grantee audience in template configuration
export const allowedGranteeRoles: UserRole[] = [
    UserRole.PI,
    UserRole.SO,
    UserRole.EXTERNAL_REVIEWER,
    UserRole.EXTERNAL_AE_DESIGNER,
    UserRole.EXTERNAL_PROJECT_MANAGER,
];

// Default audience definitions used when no phase-specific config exists.
// Mirrors the allowed lists so the defaults stay aligned with the selectable options.
export const defaultAudiences: AudienceDefinitions = {
    nih: [...allowedNihRoles],
    grantee: [...allowedGranteeRoles],
};

// Labels for displaying roles in the UI
export const roleLabels: Partial<Record<UserRole, string>> = {
    [UserRole.ORF_DTR]: "ORF-DTR",
    [UserRole.ORF_DEP]: "ORF-DEP",
    [UserRole.PO_SUPERVISOR]: "Program Officer (Supervisor)",
    [UserRole.GMS_SUPERVISOR]: "Grants Management Specialist (Supervisor)",
    [UserRole.ORF_DTR_SUPERVISOR]: "ORF-DTR (Supervisor)",
    [UserRole.PO]: "Program Officer (PO)",
    [UserRole.GMS]: "Grants Management Specialist (GMS)",
    [UserRole.PI]: "Principal Investigator (PI)",
    [UserRole.SO]: "Signing Official (SO)",
    [UserRole.EXTERNAL_REVIEWER]: "Reviewer",
    [UserRole.EXTERNAL_AE_DESIGNER]: "A/E Designer",
    [UserRole.EXTERNAL_PROJECT_MANAGER]: "Project Manager",
    [UserRole.SU]: "Super User",
};

type RoleItem = { id: UserRole; name: string };
type GroupedRoles = { key: string; items: RoleItem[] }[];

// Groups role options into NIH/Grantee sections, sorted alphabetically within each group.
// Uses isExternal from the backend DTO to determine group membership.
export function groupRoleOptions(roles: RoleOptionDto[]): GroupedRoles {
    const toItem = (r: RoleOptionDto): RoleItem => ({ id: r.value, name: r.label });

    const nih = roles.filter(r => !r.isExternal).map(toItem)
        .sort((a, b) => a.name.localeCompare(b.name));
    const grantee = roles.filter(r => r.isExternal).map(toItem)
        .sort((a, b) => a.name.localeCompare(b.name));

    const groups: GroupedRoles = [];
    if (nih.length > 0) groups.push({ key: "NIH", items: nih });
    if (grantee.length > 0) groups.push({ key: "Grantee", items: grantee });
    return groups;
}

// Groups pre-built role items into NIH/Grantee sections using isExternal lookup.
// Useful when items have custom names (e.g., with status suffixes).
export function groupRoleItems(items: RoleItem[], roles: RoleOptionDto[]): GroupedRoles {
    const externalSet = new Set(roles.filter(r => r.isExternal).map(r => r.value));

    const nih = items.filter(item => !externalSet.has(item.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    const grantee = items.filter(item => externalSet.has(item.id))
        .sort((a, b) => a.name.localeCompare(b.name));

    const groups: GroupedRoles = [];
    if (nih.length > 0) groups.push({ key: "NIH", items: nih });
    if (grantee.length > 0) groups.push({ key: "Grantee", items: grantee });
    return groups;
}
